// src/components/marketing/LandingPage.tsx
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInAnonymously } from 'firebase/auth';
import { auth, db } from '../../firebase/config';
import { collection, getDocs, query, where } from 'firebase/firestore';

import FixedColorFireworks from './FixedColorFireworks';
import SparkButton from '../common/SparkButton';
import SparkInput from '../common/SparkInput';

import sparkLogo from '../layouts/images/sparklogo9.png';
import sparkMoon from '../layouts/images/sparkmoon2.png';
import sparkLandingBg from '../layouts/images/sparklandingscreenfall25.png';
import './LandingPage.css';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  
  // Search state for hero search
  const [searchQuery, setSearchQuery] = useState('');
  const [isNavigating, setIsNavigating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Featured template IDs
  const FEATURED_TEMPLATES = {
    poolParty: 'poolPartyTheme',
    halloween: 'halloweenTheme'
  };

  // Handle featured template navigation
  const handleFeaturedTemplate = async (templateId: string) => {
    if (isNavigating) return;
    
    setIsNavigating(true);
    setError(null);
    
    try {
      const cred = await signInAnonymously(auth);
      if (!cred.user) throw new Error('Authentication failed');
      navigate(`/create-event-from-template?templateId=${templateId}`);
    } catch (err) {
      console.error('[Landing] Featured template navigation failed', err);
      setError('Unable to start event creation. Please try again.');
    } finally {
      setIsNavigating(false);
    }
  };

  // Handle template selection from showcase
  const handleTemplateSelect = async (templateId: string, projectId: string) => {
    if (isNavigating) return;
    
    setIsNavigating(true);
    setError(null);
    
    try {
      const cred = await signInAnonymously(auth);
      if (!cred.user) throw new Error('Authentication failed');
      navigate(`/create-event-from-template?templateId=${templateId}`);
    } catch (err) {
      console.error('[Landing] Template selection failed', err);
      setError('Unable to start event creation. Please try again.');
    } finally {
      setIsNavigating(false);
    }
  };

  // Handle create custom invitation
  const handleCreateCustom = async () => {
    if (isNavigating) return;
    
    setIsNavigating(true);
    setError(null);
    
    try {
      const cred = await signInAnonymously(auth);
      if (!cred.user) throw new Error('Authentication failed');
      navigate('/create-event');
    } catch (err) {
      console.error('[Landing] Create custom event failed', err);
      setError('Unable to start event creation. Please try again.');
    } finally {
      setIsNavigating(false);
    }
  };

  // Handle search submit - trigger search in TemplateSelector
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Scroll to results
    const showcaseSection = document.getElementById('template-showcase');
    if (showcaseSection) {
      showcaseSection.scrollIntoView({ behavior: 'smooth' });
    }
    // Announce to screen readers
    const announcement = searchQuery 
      ? `Searching for ${searchQuery}` 
      : 'Showing all templates';
    
    // Create a temporary element for screen reader announcement
    const announcer = document.createElement('div');
    announcer.setAttribute('aria-live', 'polite');
    announcer.setAttribute('aria-atomic', 'true');
    announcer.className = 'sr-only';
    announcer.textContent = announcement;
    document.body.appendChild(announcer);
    
    setTimeout(() => {
      document.body.removeChild(announcer);
    }, 1000);
  };

  return (
    <div className="landing-page-container">
      {/* Fireworks Effect */}
      <div className="fireworks-container">
        <FixedColorFireworks hideBackground useBlueAndPurple />
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-banner">
          <div className="error-content">
            <span>{error}</span>
            <button 
              onClick={() => setError(null)}
              className="error-close"
              aria-label="Close error message"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="landing-header">
        <div className="header-content">
          <div className="logo-section">
            <img src={sparkMoon} alt="Moon" className="header-moon" />
            <img src={sparkLogo} alt="SPARK" className="header-logo" />
          </div>
          <div className="auth-buttons">
            <SparkButton 
              size="small" 
              variant="secondary"
              onClick={() => navigate('/login')}
            >
              Sign In
            </SparkButton>
            <SparkButton 
              size="small" 
              onClick={() => navigate('/register')}
            >
              Sign Up
            </SparkButton>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background">
          <img 
            src={sparkLandingBg} 
            alt="Colorful summer party setup with flowers and decorations" 
            className="hero-bg-image"
            loading="eager"
            decoding="async"
          />
        </div>
        <div className="hero-overlay">
          <div className="hero-content">
            <h1 className="hero-title">
              Create Interactive Video Invitations - Easily Reach and Track Your Guests
            </h1>
            <p className="hero-subtitle sr-only">
              Design and send stunning digital invitations for pool parties, video game tournaments, and more
            </p>
            
            {/* Search Bar */}
            <form onSubmit={handleSearchSubmit} className="hero-search-form" role="search">
              <div className="search-container">
                <label htmlFor="hero-search" className="sr-only">
                  Search invitation templates
                </label>
                <SparkInput
                  id="hero-search"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search designs..."
                  className="hero-search-input"
                  aria-describedby="search-description"
                />
                <div id="search-description" className="sr-only">
                  Search through available invitation templates by name or description
                </div>
                <button 
                  type="submit" 
                  className="search-button"
                  aria-label="Search templates"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                    <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
                  </svg>
                </button>
              </div>
            </form>

            {/* Featured Template Buttons */}
            <div className="featured-templates">
              
              {/* HALLOWEEN FIRST — unchanged behavior (go straight to the Halloween template) */}
              <SparkButton
                onClick={async () => {
                  if (isNavigating) return;
                  setIsNavigating(true);
                  setError(null);
                  try {
                    const cred = await signInAnonymously(auth);
                    if (!cred.user) throw new Error('Authentication failed');
                    // step=1 → TemplateSelector, search=halloween → pre-filled
                    navigate('/create-event-from-template?step=1&search=halloween');
                  } catch (err) {
                    console.error('[Landing] Halloween shortcut failed', err);
                    setError('Unable to start event creation. Please try again.');
                  } finally {
                    setIsNavigating(false);
                  }
                }}
                className="featured-button"
                size="large"
                disabled={isNavigating}
              >
                {isNavigating ? 'Starting…' : 'Halloween Invitations'}
              </SparkButton>

              {/* WIZARDING — goes to TemplateSelector with "wizard" prefilled */}
              <SparkButton
                onClick={async () => {
                  if (isNavigating) return;
                  setIsNavigating(true);
                  setError(null);
                  try {
                    const cred = await signInAnonymously(auth);
                    if (!cred.user) throw new Error('Authentication failed');
                    // step=1 → TemplateSelector, search=wizard → pre-filled
                    navigate('/create-event-from-template?step=1&search=wizard');
                  } catch (err) {
                    console.error('[Landing] Wizarding shortcut failed', err);
                    setError('Unable to start event creation. Please try again.');
                  } finally {
                    setIsNavigating(false);
                  }
                }}
                className="featured-button"
                size="large"
                disabled={isNavigating}
              >
                {isNavigating ? 'Starting…' : 'Wizarding Invitations'}
              </SparkButton>
            </div>
          </div>
        </div>
      </section>

      {/* Template Showcase Section */}
      <section id="template-showcase" className="template-showcase">
        <div className="showcase-container">
          <div className="showcase-header">
            <h2>What kind of party are you throwing?</h2>
            {searchQuery && (
              <p className="search-results-text">
                Searching for "{searchQuery}"
              </p>
            )}
          </div>
          
          <TemplateSelectorWrapper
            searchQuery={searchQuery}
            onTemplateSelect={handleTemplateSelect}
          />
        </div>
      </section>

      {/* Footer Actions */}
      <section className="footer-actions">
        <div className="footer-content">
          <SparkButton
            onClick={handleCreateCustom}
            size="large"
            className="cta-button"
            disabled={isNavigating}
          >
            {isNavigating ? 'Starting...' : 'Create New Event'}
          </SparkButton>
        </div>
      </section>
    </div>
  );
};

// Wrapper component to integrate search with TemplateSelector
interface TemplateSelectorWrapperProps {
  searchQuery: string;
  onTemplateSelect: (templateId: string, projectId: string) => void;
}

const TemplateSelectorWrapper: React.FC<TemplateSelectorWrapperProps> = ({
  searchQuery,
  onTemplateSelect,
}) => {
  const [templates, setTemplates] = useState<any[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch templates
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        const templatesRef = collection(db, 'templates');
        const q = query(templatesRef, where('isActive', '==', true));
        const snapshot = await getDocs(q);
        
        const loadedTemplates = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        
        setTemplates(loadedTemplates);
      } catch (error) {
        console.error('Error fetching templates:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  // Filter templates based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredTemplates(templates);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = templates.filter((template) => {
      const nameMatch = template.name?.toLowerCase().includes(query) || false;
      const descriptionMatch = template.description?.toLowerCase().includes(query) || false;
      return nameMatch || descriptionMatch;
    });
    
    setFilteredTemplates(filtered);
  }, [searchQuery, templates]);

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner" />
        <p>Loading templates...</p>
      </div>
    );
  }

  return (
    <div className="template-selector-wrapper">
      {/* Custom template grid */}
      <div className="templates-grid">
        {filteredTemplates.map((template) => (
          <div
            key={template.id}
            className="template-card"
            onClick={() => onTemplateSelect(template.id, template.blingsProjectId || '')}
          >
            <img
              src={template.thumbnailUrl || template.previewThumb || '/api/placeholder/320/180'}
              alt={template.name || 'Template'}
              className="template-thumbnail"
            />
            <div className="template-content">
              <h4 className="template-title">{template.name}</h4>
              {template.description && (
                <p className="template-description">{template.description}</p>
              )}
            </div>
          </div>
        ))}
      </div>
      
      {filteredTemplates.length === 0 && searchQuery && (
        <div className="no-results">
          <p>No templates found for "{searchQuery}"</p>
          <p>Try searching for something else or browse all templates.</p>
        </div>
      )}
    </div>
  );
};

export default LandingPage;